import React, { useState, useEffect, useMemo } from 'react';

import { Col, Dropdown, Row } from 'antd';

import styled from 'styled-components';

import { GetTimestamp } from '@libs/NwMoment';
import { checkTenant } from '@libs/NwUtils';

import { useProjectContext, useProjectContextDispatch, reloadProjectAccountingData } from '@contexts/ProjectContext';
import { useUserPermission } from '@contexts/UserContext';

import { NwAddButton } from '@components/Gui/NwButton';
import ProjectGroupedTransactionsList from '@components/Project/ProjectTransactions/Transactions/ProjectGroupedTransactionsList';
import ProjectTransactionsCard from '@components/Project/ProjectTransactions/Transactions/ProjectTransactionsCard';

import AccountingDetailsCard from './AccountingDetails/AccountingDetailsCard';
import ProjectInvoicesCard from './Invoices/ProjectInvoicesCard';
import AddProjectTransactionDrawer from './Transactions/AddProjectTransactionDrawer';
import ProjectUsagesCard from './Usages/ProjectUsagesCard';

const Container = styled.div`
    .ant-card-body-content {
        padding: 0 !important;
    }

    div.add-button-container {
        padding: 1rem 2rem;
        border-bottom: ${(props) => props.theme.DefaultBorder};
        display: flex;
        justify-content: flex-end;
    }
`;

const ProjectTransactions = ({ projectId: projectIdProp, eventData, mode = 'project', readOnly }) => {
    const contextData = useProjectContext();
    const projectDispatch = useProjectContextDispatch();
    const projectId = projectIdProp || contextData?.projectId;

    const [transactionGroup, setTransactionGroup] = useState();
    const [transactionDrawer, setTransactionDrawer] = useState(false);
    const [lastUpdate, setLastUpdate] = useState(GetTimestamp());

    const accessAccounting = useUserPermission('AccessAccounting');

    // Get transactions and project data from context for checking rules
    const projectTransactions = contextData?.transactionsGrouped || [];
    const projectData = contextData?.projectData;

    // Check if there are blocking transactions (JobFee, Product, JobAdvance, AgFee, JobCredit)
    const hasBlockingTransactions = useMemo(() => {
        if (!projectTransactions || !projectTransactions.length) return false;

        const blockingFamilies = ['JobFee', 'Product', 'JobAdvance', 'AgFee', 'JobCredit'];

        for (const modelTransaction of projectTransactions) {
            if (modelTransaction.Dates && modelTransaction.Dates.length) {
                for (const date of modelTransaction.Dates) {
                    if (date.Transactions && date.Transactions.length) {
                        const hasBlocking = date.Transactions.some((transaction) =>
                            blockingFamilies.includes(transaction.Family)
                        );
                        if (hasBlocking) return true;
                    }
                }
            }
        }
        return false;
    }, [projectTransactions]);

    // Check if there are CommissionAsMotherAgency transactions
    const hasCommissionAsMotherAgency = useMemo(() => {
        if (!projectTransactions || !projectTransactions.length) return false;

        for (const modelTransaction of projectTransactions) {
            if (modelTransaction.Dates && modelTransaction.Dates.length) {
                for (const date of modelTransaction.Dates) {
                    if (date.Transactions && date.Transactions.length) {
                        const hasCommAsMothAg = date.Transactions.some(
                            (transaction) => transaction.Family === 'CommAsMothAg'
                        );
                        if (hasCommAsMothAg) return true;
                    }
                }
            }
        }
        return false;
    }, [projectTransactions]);

    // Check if project has events of type 'ext' (required for CommissionAsMotherAgency)
    const hasExtEvents = useMemo(() => {
        if (!projectData?.Events) return false;
        return projectData.Events.some((event) => event.TypeName?.toLowerCase() === 'ext');
    }, [projectData]);

    // Check if project has events of type 'job' or 'casting' (required for JobFee)
    const hasJobOrCastingEvents = useMemo(() => {
        if (!projectData?.Events) return false;
        return projectData.Events.some(
            (event) => event.TypeName?.toLowerCase() === 'job' || event.TypeName?.toLowerCase() === 'casting'
        );
    }, [projectData]);

    // Fetch accounting details and transactions on mount or when projectId changes
    useEffect(() => {
        if (!projectId) return;

        reloadProjectAccountingData(projectId, projectDispatch);
    }, [projectId, lastUpdate, projectDispatch]);

    const handleCloseTransactionDrawer = () => {
        setTransactionGroup();
        setTransactionDrawer(false);
        setLastUpdate(GetTimestamp());
    };

    const handleShowTransactionDrawer = ({ key }) => {
        setTransactionGroup(key);
        setTransactionDrawer(true);
    };

    const addTransactionMenuItems = () => {
        const isEliteBooker = checkTenant('elitemodels') && !accessAccounting;
        const items = [
            {
                key: 'Fee',
                label: 'Fee',
                disabled: hasCommissionAsMotherAgency || !hasJobOrCastingEvents,
            },
            { key: 'Expense', label: 'Expense' },
            { key: 'Credit', label: 'Credit', disabled: hasCommissionAsMotherAgency },
            {
                key: 'CommissionAsMotherAgency',
                label: 'Commission As Mother Agency',
                disabled: hasBlockingTransactions || !hasExtEvents,
            },
        ];
        //if isElite and user does not have access to accounting I remove the Credit option
        if (isEliteBooker) {
            items.splice(2, 1);
        }
        return items;
    };

    return (
        <Container>
            {mode === 'event' ? (
                <>
                    <div className="add-button-container">
                        <Dropdown
                            key="addTransaction"
                            menu={{
                                onClick: handleShowTransactionDrawer,
                                items: addTransactionMenuItems(),
                            }}
                        >
                            <div className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
                                <NwAddButton ghost size="small" label="add a transaction" />
                            </div>
                        </Dropdown>
                    </div>
                    <ProjectGroupedTransactionsList event={eventData} readOnly={readOnly} />
                </>
            ) : (
                <div className="nw-cards-grid">
                    <Row gutter={10} type="flex">
                        <Col span={16}>
                            <ProjectTransactionsCard readOnly={readOnly} />
                        </Col>
                        <Col span={8}>
                            <div>
                                <AccountingDetailsCard />
                            </div>
                            <div style={{ marginTop: 10 }}>
                                <ProjectInvoicesCard />
                            </div>
                            <div style={{ marginTop: 10 }}>
                                <ProjectUsagesCard readOnly={readOnly} />
                            </div>
                        </Col>
                    </Row>
                </div>
            )}
            {transactionDrawer && (
                <AddProjectTransactionDrawer
                    transactionGroup={transactionGroup}
                    projectId={projectId}
                    onClose={handleCloseTransactionDrawer}
                />
            )}
        </Container>
    );
};

export default ProjectTransactions;
